import { useTranslation } from "@/hooks/useTranslation";
import { useAppContext } from "@/App";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, RefreshCw, ArrowLeft, LogOut, Upload, BarChart3,
  Users, CalendarDays, Route, FileText, MapPin, Coffee, Bird
} from "lucide-react";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import WorkdayButton from "@/components/shared/WorkdayButton";
import { TutorialToggle } from "@/components/shared/TutorialLedger";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import api from "@/lib/api";

export default function PlannerHeader({
  isRefreshing, onRefresh, calNotifCount,
  showImport, setShowImport, showCalendar, setShowCalendar,
  showRouteBuilder, setShowRouteBuilder, showMap, setShowMap,
  funMode, setFunMode, setShowAnalytics, setShowHubAdmin,
  setShowTutorial, showTutorial, loadCalendarAndRoutes, generatePDF,
}) {
  const { session, setSession, auth, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();

  const toolbarItems = [
    { tid: "import-data-btn", icon: Upload, action: () => setShowImport(!showImport), active: showImport, color: "lime", label: "Import" },
    { tid: "analytics-btn", icon: BarChart3, action: () => setShowAnalytics(true), color: "lime", label: "Analytics" },
    { tid: "hub-admin-btn", icon: Users, action: () => setShowHubAdmin(true), color: "amber", label: "Admin" },
    { tid: "calendar-btn", icon: CalendarDays, action: () => { setShowCalendar(!showCalendar); if (!showCalendar) loadCalendarAndRoutes(); }, active: showCalendar, color: "blue", notif: calNotifCount, label: "Calendar" },
    { tid: "route-builder-btn", icon: Route, action: () => { setShowRouteBuilder(!showRouteBuilder); if (!showRouteBuilder) loadCalendarAndRoutes(); }, active: showRouteBuilder, color: "amber", label: "Routes" },
    { tid: "pdf-export-btn", icon: FileText, color: "slate", label: "Export", isPdf: true },
    { tid: "planner-map-btn", icon: MapPin, action: () => setShowMap(!showMap), active: showMap, color: "blue", label: "Map" },
    { tid: "fun-request-btn", icon: Coffee, action: () => setFunMode(!funMode), active: funMode, color: "amber", label: "Fun" },
  ];

  return (
    <header className="bg-white px-4 md:px-8 py-4 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center text-slate-950 shadow-sm">
          <LayoutDashboard className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">{t("planner_dashboard")}</h1>
          <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{session?.hubName}</p>
        </div>
        <DashboardSwitcher currentRole="planner" />
      </div>
      <div className="flex items-center gap-2">
        <WorkdayButton role="planner" />
        {/* Pigeon Toolbar */}
        <div className="relative group" data-testid="pigeon-toolbar">
          <button data-testid="pigeon-toolbar-trigger"
            className="w-11 h-11 bg-red-600 hover:bg-red-500 rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-red-600/30 transition-all duration-300 hover:scale-110 active:scale-95">
            <Bird className="w-6 h-6 transition-transform duration-300 group-hover:rotate-12" />
          </button>
          <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60]">
            <div className="flex flex-col gap-1.5 bg-white/95 backdrop-blur-lg border border-slate-200 rounded-xl p-2 shadow-xl min-w-[48px]">
              {toolbarItems.map((btn, i) => {
                const BtnIcon = btn.icon;
                const activeClass = btn.active
                  ? `bg-${btn.color}-500 text-${btn.color === "slate" ? "white" : "slate-950"} shadow-sm`
                  : "text-slate-400 hover:text-slate-900 hover:bg-slate-50";
                return btn.isPdf ? (
                  <div key={btn.tid} className="relative group/pdf"
                    style={{ animationDelay: `${i * 40}ms`, animation: "fadeSlideIn 0.3s ease both" }}>
                    <button data-testid={btn.tid}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeClass}`}>
                      <BtnIcon className="w-4 h-4 shrink-0" /> <span>{btn.label}</span>
                    </button>
                    <div className="absolute right-full top-0 mr-1 hidden group-hover/pdf:block">
                      <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[130px]">
                        <button data-testid="pdf-sites" onClick={() => generatePDF("sites")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-lime-500" /> Sites
                        </button>
                        <button data-testid="pdf-machines" onClick={() => generatePDF("machines")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                          <BarChart3 className="w-3.5 h-3.5 text-amber-500" /> Machines
                        </button>
                        <button data-testid="pdf-drivers" onClick={() => generatePDF("drivers")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-red-500" /> Drivers
                        </button>
                        <div className="border-t border-slate-100" />
                        <button data-testid="pdf-full-report" onClick={() => window.open(api.getReportUrl(session?.hubId, "all") + `&token=${auth?.jwt}`, "_blank")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-red-500 hover:bg-red-50 flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5" /> Full Report
                        </button>
                        <button data-testid="pdf-activity-log" onClick={() => window.open(api.getReportUrl(session?.hubId, "activity") + `&token=${auth?.jwt}`, "_blank")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-500 hover:bg-slate-50 flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5" /> Activity Log
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button key={btn.tid} data-testid={btn.tid} onClick={btn.action}
                    style={{ animationDelay: `${i * 40}ms`, animation: "fadeSlideIn 0.3s ease both" }}
                    className={`relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeClass}`}>
                    <BtnIcon className="w-4 h-4 shrink-0" /> <span>{btn.label}</span>
                    {btn.notif > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-black flex items-center justify-center rounded-full">{btn.notif}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
        <LanguageSwitcher compact />
        <button data-testid="planner-refresh-btn" onClick={onRefresh}
          className={`p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all ${isRefreshing ? "animate-spin-slow" : ""}`}>
          <RefreshCw className="w-4 h-4" />
        </button>
        <button data-testid="back-to-lobby-btn" onClick={() => { setSession(null); nav("/portal"); }}
          className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button data-testid="planner-exit-btn" onClick={() => { logout(); nav("/"); }}
          className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}
